/**
 * Darstellung von Zahlen, Betraegen und Daten in deutscher Schreibweise.
 *
 * Gerechnet wird in der Anwendung; hier wird nur dargestellt. Betraege kommen
 * als Zeichenkette oder Zahl an - beides wird ueber `Number` gefuehrt, weil die
 * Anzeige keine Cent-genaue Arithmetik betreibt.
 */

const geldformat = new Intl.NumberFormat('de-DE', {
  style: 'currency',
  currency: 'EUR',
})

export function geld(wert: number | string | null | undefined): string {
  if (wert === null || wert === undefined) {
    return '—'
  }
  return geldformat.format(Number(wert))
}

export function zahl(wert: number | string | null | undefined, stellen = 3): string {
  if (wert === null || wert === undefined) {
    return '—'
  }
  return new Intl.NumberFormat('de-DE', {
    minimumFractionDigits: 0,
    maximumFractionDigits: stellen,
  }).format(Number(wert))
}

/**
 * Eine Menge mit Einheit.
 *
 * Kilowattstunden werden ganz ausgewiesen: Tausendstel einer Kilowattstunde sagen
 * niemandem etwas, und der Beleg rundet ebenso. Wer eine feinere Einheit anzeigt,
 * gibt die Stellen mit.
 */
export function menge(
  wert: number | string | null | undefined,
  einheit = 'kWh',
  stellen = 0,
): string {
  if (wert === null || wert === undefined) {
    return '—'
  }
  return `${zahl(wert, stellen)} ${einheit}`
}

export function prozent(wert: number | string | null | undefined, stellen = 1): string {
  if (wert === null || wert === undefined) {
    return '—'
  }
  return `${zahl(wert, stellen)} %`
}

export function datum(wert: string | null | undefined): string {
  if (!wert) {
    return '—'
  }
  const tag = wert.length > 10 ? new Date(wert) : new Date(`${wert}T00:00:00`)
  return tag.toLocaleDateString('de-DE')
}

export function zeitpunkt(wert: string | null | undefined): string {
  if (!wert) {
    return '—'
  }
  return new Date(wert).toLocaleString('de-DE')
}

/** Preisbasis in der gebraeuchlichen Schreibweise. */
const preisbasen: Record<string, string> = {
  CT_PRO_KWH: 'ct/kWh',
  EUR_PRO_KWH: 'EUR/kWh',
  EUR_PRO_MWH: 'EUR/MWh',
  EUR_PRO_JAHR: 'EUR/Jahr',
  EUR_PRO_MONAT: 'EUR/Monat',
  EUR_PRO_TAG: 'EUR/Tag',
  EUR_PRO_KW_JAHR: 'EUR/(kW*a)',
  EUR_PRO_KW_MONAT: 'EUR/(kW*Monat)',
  CT_PRO_KVARH: 'ct/kvarh',
  EUR_PRO_STUECK: 'EUR/Stk',
}

export function preisbasis(wert: string | null | undefined): string {
  if (!wert) {
    return ''
  }
  return preisbasen[wert] ?? wert
}

/** Mengeneinheit in der gebraeuchlichen Schreibweise. */
export function einheit(wert: string | null | undefined): string {
  if (!wert) {
    return ''
  }
  const bekannte: Record<string, string> = {
    KWH: 'kWh',
    MWH: 'MWh',
    KW: 'kW',
    KVARH: 'kvarh',
    TAG: 'Tage',
    MONAT: 'Monate',
    JAHR: 'Jahre',
    STUECK: 'Stk',
  }
  return bekannte[wert] ?? wert.toLowerCase()
}
